import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_HexColorPickerFromImage = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/hex-color-picker-from-image',
			name: 'Hex Color Picker from Image — Get Exact HEX Codes by Lift',
			description:
				'Upload any photo and get the exact HEX code of any color with a live eyedropper — plus RGB and HSL. Identify colors from any image instantly. Try Lift today.',
			url: 'https://liftapp.ai/hex-color-picker-from-image',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/hex-color-picker-from-image#product' },
				{ '@id': 'https://liftapp.ai/hex-color-picker-from-image#howto' },
				{ '@id': 'https://liftapp.ai/hex-color-picker-from-image#benefits' },
				{ '@id': 'https://liftapp.ai/hex-color-picker-from-image#faq' },
				{ '@id': 'https://liftapp.ai/hex-color-picker-from-image#why-lift' },
				{
					'@id': 'https://liftapp.ai/hex-color-picker-from-image#explore-tools',
				},
				{ '@id': 'https://liftapp.ai/hex-color-picker-from-image#all-in-one' },
			],
		},
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/hex-color-picker-from-image#product',
			name: 'Hex Color Picker from Image',
			description:
				'Upload a photo, hover the eyedropper over any pixel, and get the exact HEX code instantly — along with RGB and HSL values ready to copy. Try Lift today.',
			url: 'https://liftapp.ai/hex-color-picker-from-image',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/hex-color-picker-from-image#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photo',
					text: 'Add any image to the editor and the live eyedropper activates instantly on the canvas.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Hover to preview the HEX code',
					text: 'The eyedropper tracks every pixel and displays the HEX code in real time as you move across the image.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Click to lock your color',
					text: 'Tap any spot to fix the selection; the exact HEX, RGB, and HSL values appear instantly in the side panel.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Copy and use anywhere',
					text: 'Copy the HEX code with one click and use it directly in CSS, Figma, Illustrator, or any creative tool.',
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/hex-color-picker-from-image#benefits',
			name: 'Who can benefit from the Lift Hex Color Picker?',
			description:
				'Lift Hex Color Picker helps web designers, brand designers, marketers, social media creators, and print designers identify exact HEX codes from any photo or screenshot.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: "Web designers & developers — Get pixel-accurate HEX codes from any screenshot or mockup to implement directly in CSS. Lift's eyedropper pulls the exact value without switching between tools.",
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Brand & graphic designers — Match colors across platforms by identifying exact HEX values from reference images. Hover over any spot and lock the precise code for Figma, Illustrator, or Photoshop.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Digital marketers & ad teams — Source consistent brand colors from existing campaign assets without manual color matching. The live eyedropper reads any tone from any photo in seconds.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Social media designers — Replicate trending visual aesthetics by identifying exact colors from any inspiration image. Lift surfaces the HEX code of any pixel on the spot.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Print & packaging designers — Identify exact color references from product photography to brief print vendors accurately. Lift returns a precise HEX code for any shade in any image.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Lift hex color picker from image — mobile view of tropical flower with floating HEX color swatches',
				description:
					'Mobile phone showing Lift color picker on a bird of paradise flower photo with extracted HEX color chips',
			},
		},
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/hex-color-picker-from-image#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift Hex Color Picker?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "The Lift Hex Color Picker is an image-based tool that identifies the exact HEX code of any color in your photo using a live eyedropper. It also displays the same color in RGB and HSL, and generates a palette of the image's dominant colors.",
					},
				},
				{
					'@type': 'Question',
					name: 'How does the Lift Hex Color Picker work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload a photo and hover the eyedropper over any area to preview the HEX code in real time. Click to lock your selection and instantly copy the HEX, RGB, or HSL value from the side panel.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I get the color in RGB and HSL as well as HEX?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — every color you pick is displayed in HEX, RGB, and HSL simultaneously in the panel. Each format has its own copy button, so you grab exactly the value your workflow needs.',
					},
				},
				{
					'@type': 'Question',
					name: 'What image formats does the Lift Hex Color Picker support?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'You can upload JPG, PNG, WEBP, and HEIC files in any size or resolution. The tool works equally well with product shots, screenshots, landscapes, and scanned artwork.',
					},
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/hex-color-picker-from-image#why-lift',
			name: 'Why Lift Hex Color Picker',
			description:
				'High-quality results, intelligent rendering, cross-platform support, and budget-friendly pricing.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Lift hex color picker from image — aurora borealis photo with emerald HEX code and extracted palette',
				description:
					'Northern lights aurora borealis photo with Lift eyedropper showing HEX code #1DB87A and a green-purple palette',
			},
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/hex-color-picker-from-image#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/hex-color-picker-from-image#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Hex Color Picker from Image',
					item: 'https://liftapp.ai/hex-color-picker-from-image',
				},
			],
		},
	],
};
